/**
 * CrV3H5SpeechBridge — H5 PTT 本地 SDK + uplink SentenceItem envelope（D20）
 */

import { ref } from 'vue'
import type { LiveSpeechEnvelopeMessage, ViewerSubtitleRow } from '../types'
import { buildLiveSpeechEnvelope } from '../utils/uplink-live-speech'
import { enrichLocalViewerRow, shortLangCode } from '../adapters/live-speech-adapter'
import { resolvePttTargetLanguages } from '../utils/resolve-ptt-target-languages'
import {
  applyPhraseListToRecognizer,
  applySpeechTranslationSegmentation,
  buildTranslationsFromAzureCodes,
  resolveEffectiveAzureTargetCodes,
} from '@/modules/speech-translation/utils/azure-speech-sdk-shared'
import type { ViewerLanguageItem } from '@/modules/speech-translation/api/viewer'

export interface CrV3SessionLanguagesContext {
  source: ViewerLanguageItem
  targets: ViewerLanguageItem[]
}

export interface UseCrV3H5SpeechBridgeOptions {
  speakerName: string
  role?: string
  /** PTT 发言语种 BCP（join session languageCode2） */
  sourceLanguageCode2: string
  getSessionLanguages: () => CrV3SessionLanguagesContext
  /** session 源语短码（lang/select 后以 WS 最新值为准） */
  getSessionSourceShort: () => string
  /** session 目标语短码列表 */
  getSessionTargetShorts: () => string[]
  /** STT 启动前注入的术语短语（HTTP glossary，D19） */
  getGlossaryPhrases?: () => string[]
  onUplink: (message: LiveSpeechEnvelopeMessage) => void
  onLocalPreview?: (row: ViewerSubtitleRow) => void
  onLocalFragment?: (row: ViewerSubtitleRow) => void
  /** 可选：覆盖 segmentation silence（runtime-config） */
  segmentationSilenceTimeoutMs?: number
}

function rowFromSpeech(
  sourceText: string,
  translations: Record<string, string>,
  speakerName: string,
  isFinal: boolean,
): ViewerSubtitleRow {
  return {
    source_text: sourceText,
    translations,
    speaker: speakerName,
    ts: isFinal ? String(Date.now()) : undefined,
  }
}

function hasEnvelopeContent(message: LiveSpeechEnvelopeMessage): boolean {
  const data = message.data
  return Boolean(data.readText?.text?.trim() || data.listenText?.length)
}

/** 短码 → session 已知 BCP（languageCode2） */
function buildShortToCode2Map(langs: CrV3SessionLanguagesContext): Map<string, string> {
  const map = new Map<string, string>()
  const sourceShort = shortLangCode(langs.source.languageCode)
  const sourceBcp = langs.source.languageCode2 || langs.source.languageCode
  if (sourceShort) {
    map.set(sourceShort, sourceBcp)
  }
  for (const target of langs.targets) {
    const short = shortLangCode(target.languageCode)
    if (!short) {
      continue
    }
    map.set(short, target.languageCode2 || target.languageCode)
  }
  return map
}

function mapShortTargetsToCode2(
  shortTargets: string[],
  shortToCode2: Map<string, string>,
): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const short of shortTargets) {
    const normalizedShort = shortLangCode(short)
    if (!normalizedShort || seen.has(normalizedShort)) {
      continue
    }
    seen.add(normalizedShort)
    const bcp = shortToCode2.get(normalizedShort)
    if (!bcp || !bcp.includes('-')) {
      continue
    }
    result.push(bcp)
  }
  return result
}

export function useCrV3H5SpeechBridge(options: UseCrV3H5SpeechBridgeOptions) {
  const active = ref(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let recognizer: any = null

  async function start(
    token: string,
    region: string,
    audioStream: MediaStream,
  ): Promise<void> {
    await stop()
    const speechsdk = await import('microsoft-cognitiveservices-speech-sdk')
    const config = speechsdk.SpeechTranslationConfig.fromAuthorizationToken(token, region)
    config.speechRecognitionLanguage = options.sourceLanguageCode2

    const sessionLangs = options.getSessionLanguages()
    const sourceBcp = sessionLangs.source.languageCode2 || sessionLangs.source.languageCode
    const shortTargets = resolvePttTargetLanguages({
      pttSourceLanguageCode: options.sourceLanguageCode2,
      classroomWorkLangCode: sourceBcp,
      listenTargetLanguageCodes: sessionLangs.targets.map((t) => t.languageCode),
    })
    const shortToCode2 = buildShortToCode2Map(sessionLangs)
    const targetBcps = mapShortTargetsToCode2(shortTargets, shortToCode2)
    const effectiveAzureCodes = resolveEffectiveAzureTargetCodes(targetBcps)
    const azureTargets = new Set<string>()
    for (const code of effectiveAzureCodes) {
      if (code && !azureTargets.has(code)) {
        azureTargets.add(code)
        config.addTargetLanguage(code)
      }
    }

    applySpeechTranslationSegmentation(
      config,
      speechsdk,
      options.segmentationSilenceTimeoutMs,
    )

    const audioConfig = speechsdk.AudioConfig.fromStreamInput(audioStream)
    recognizer = new speechsdk.TranslationRecognizer(config, audioConfig)

    const phrases = options.getGlossaryPhrases?.() ?? []
    applyPhraseListToRecognizer(recognizer, phrases, speechsdk)

    const role = options.role ?? 'viewer'
    const voiceLangShort = shortLangCode(options.sourceLanguageCode2)

    recognizer.recognizing = (_: unknown, e: { result: { reason: number; text: string; translations: Map<string, string> } }) => {
      if (e.result.reason !== speechsdk.ResultReason.TranslatingSpeech) {
        return
      }
      const sourceText = e.result.text || ''
      const translations = buildTranslationsFromAzureCodes(
        e.result.translations,
        targetBcps,
        effectiveAzureCodes,
        { mapOutputKey: (lang) => shortLangCode(lang) },
      )
      const envelope = buildLiveSpeechEnvelope({
        role,
        speakerName: options.speakerName,
        voiceText: sourceText,
        voiceLangShort,
        translations,
        sessionSourceShort: options.getSessionSourceShort(),
        sessionTargetShorts: options.getSessionTargetShorts(),
        isFinal: false,
      })
      if (hasEnvelopeContent(envelope)) {
        options.onUplink(envelope)
      }
      options.onLocalPreview?.(
        enrichLocalViewerRow(
          rowFromSpeech(sourceText, translations, options.speakerName, false),
          voiceLangShort,
        ),
      )
    }

    recognizer.recognized = (_: unknown, e: { result: { reason: number; text: string; translations: Map<string, string> } }) => {
      if (e.result.reason !== speechsdk.ResultReason.TranslatedSpeech) {
        return
      }
      const sourceText = e.result.text || ''
      if (!sourceText.trim()) {
        return
      }
      const translations = buildTranslationsFromAzureCodes(
        e.result.translations,
        targetBcps,
        effectiveAzureCodes,
        { mapOutputKey: (lang) => shortLangCode(lang) },
      )
      const envelope = buildLiveSpeechEnvelope({
        role,
        speakerName: options.speakerName,
        voiceText: sourceText,
        voiceLangShort,
        translations,
        sessionSourceShort: options.getSessionSourceShort(),
        sessionTargetShorts: options.getSessionTargetShorts(),
        isFinal: true,
        durationMs: 800,
      })
      if (hasEnvelopeContent(envelope)) {
        options.onUplink(envelope)
      }
      options.onLocalFragment?.(
        enrichLocalViewerRow(
          rowFromSpeech(sourceText, translations, options.speakerName, true),
          voiceLangShort,
        ),
      )
    }

    await recognizer.startContinuousRecognitionAsync()
    active.value = true
  }

  async function stop(): Promise<void> {
    if (!recognizer) {
      active.value = false
      return
    }
    try {
      await recognizer.stopContinuousRecognitionAsync()
    } catch {
      // ignore stop races
    }
    recognizer.close()
    recognizer = null
    active.value = false
  }

  return { active, start, stop }
}
